import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { truecareWelcomeAnalyticsApi } from '@/api/truecareWelcomeAnalytics';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold brand-stat-value tabular-nums">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'READ' || status === 'DELIVERED') return 'default';
  if (status === 'SENT') return 'secondary';
  if (status === 'FAILED') return 'destructive';
  return 'outline';
}

export default function TrueCareWelcomeAnalyticsPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['truecare-welcome-analytics', from, to],
    queryFn: () =>
      truecareWelcomeAnalyticsApi.getDashboard({
        ...(from ? { from: new Date(from).toISOString() } : {}),
        ...(to ? { to: new Date(`${to}T23:59:59`).toISOString() } : {}),
      }),
  });

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold brand-page-title">TrueCare onboarding WhatsApp</h1>
        <p className="text-sm text-muted-foreground">
          Registration welcome message delivery and app adoption from the registration funnel.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Date range</CardTitle>
          <CardDescription>Filter by patient registration / notification date</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <div className="space-y-1">
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading analytics…
        </div>
      ) : data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Stat label="Total registrations" value={data.totalRegistrations} />
            <Stat label="WhatsApp sent" value={data.whatsappSent} />
            <Stat
              label="Delivery rate"
              value={`${data.deliveryRatePct}%`}
              hint={`${data.whatsappDelivered} delivered`}
            />
            <Stat
              label="Read rate"
              value={`${data.readRatePct}%`}
              hint={`${data.whatsappRead} read`}
            />
            <Stat label="App download clicks" value={data.appDownloadClicks} />
            <Stat
              label="App installs"
              value={data.appInstallsFromRegistration}
              hint={`${data.installRatePct}% of sent`}
            />
          </div>

          {data.whatsappFailed > 0 ? (
            <p className="text-sm text-amber-700">{data.whatsappFailed} message(s) failed after retries.</p>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent welcome messages</CardTitle>
              <CardDescription>
                {isFetching ? 'Refreshing…' : `Last updated ${new Date(data.generatedAt).toLocaleString()}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>UHID</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead>Read</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recentNotifications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No welcome messages yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.recentNotifications.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-medium">{row.patientName}</TableCell>
                        <TableCell>{row.uhid ?? '—'}</TableCell>
                        <TableCell>{row.mobile}</TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                        </TableCell>
                        <TableCell>
                          {row.sentAt ? new Date(row.sentAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>
                          {row.deliveredAt ? new Date(row.deliveredAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>
                          {row.readAt ? new Date(row.readAt).toLocaleString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
